import { useEffect, useState, useMemo } from 'react';
import axios, { AxiosResponse } from 'axios';
import { Genre } from './@enums/genre.enum';
import { Instrument } from './@enums/instrument.enum';
import { Tempo } from './@enums/tempo.enum';
import { Density } from './@enums/density.enum';
import { Soundscape } from './@enums/soundscape.enum';
import { LocalStorage } from '@/lib/helpers/local-storage/local-storage';

/* ================================== TYPES ================================= */

export type TFetchSessionResponse = {
  id: string;
  totalDurationMs: number;
  trackUrl: string;
  shareCode: string;
} | null;

export type TUseInteractivePlayer = {
  genre: {
    get?: Genre | undefined;
    set: (value?: Genre) => void;
  };
  instrument: {
    get?: Instrument | undefined;
    set: (value?: Instrument) => void;
  };
  tempo: {
    get?: Tempo | undefined;
    set: (value?: Tempo) => void;
  };
  density: {
    get?: Density | undefined;
    set: (value?: Density) => void;
  };
  soundscape: {
    get?: Soundscape | undefined;
    set: (value?: Soundscape) => void;
  };
  session: {
    fetch: () => Promise<void>;
    fetchByShareCode: (shareCode: string) => Promise<void>;
    get: TFetchSessionResponse;
    isLoading: boolean;
  };
};

// const API_URL = 'http://localhost:7002/session/get';
const API_URL = process.env.NEXT_PUBLIC_API_URL || ''; // 'https://ehbz9kqaxh.us-east-2.awsapprunner.com/session/get';

/* ========================================================================== */
/*                                    HOOK                                    */
/* ========================================================================== */
export function useInteractivePlayer(): TUseInteractivePlayer {
  const [genre, setGenre] = useState<Genre | undefined>(undefined);
  const [instrument, setInstrument] = useState<Instrument | undefined>(
    undefined,
  );
  const [tempo, setTempo] = useState<Tempo | undefined>(undefined);
  const [density, setDensity] = useState<Density | undefined>(undefined);
  const [soundscape, setSoundscape] = useState<Soundscape | undefined>(
    undefined,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [session, setSession] = useState<TFetchSessionResponse>(null);

  const localStorage = useMemo(() => new LocalStorage(), []);

  const [userId, setUserId] = useState<string>(() => {
    const storedUserId = localStorage.getString('userId', '');
    if (storedUserId) {
      return storedUserId;
    } else {
      const newUserId = crypto.randomUUID();
      localStorage.setString('userId', newUserId);
      return newUserId;
    }
  });

  useEffect(() => {
    if (!userId) {
      const newUserId = crypto.randomUUID();
      setUserId(newUserId);
      localStorage.setString('userId', newUserId);
    }
  }, [userId, localStorage]);

  const fetchSessionByShareCode = async (shareCode: string) => {
    console.log('fetchSessionByShareCode', API_URL, shareCode);
    setIsLoading(true);

    try {
      const response: AxiosResponse<TFetchSessionResponse> = await axios.get(
        `${API_URL}/${shareCode}`,
      );
      console.log('response', response);
      if (response.data) {
        setSession(response.data);

        // Update the state with the fetched session data
        setGenre(response.data.genre);
        setInstrument(response.data.instrument);
        setTempo(response.data.tempo);
        setDensity(response.data.density);
        setSoundscape(response.data.soundscape);
      }
    } catch (error) {
      console.error('Error fetching session by share code:', error);
      // Handle error appropriately
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSession = async () => {
    console.log('fetchSession', API_URL);
    setIsLoading(true);

    const startTime = Date.now();
    const minLoadingTime = 3000; // 3 seconds

    try {
      const response: AxiosResponse<TFetchSessionResponse> = await axios.post(
        API_URL,
        {
          genre,
          instrument,
          tempo,
          density,
          soundscape,
          userId,
        },
      );

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      // Wait for the remaining time if the request was faster than minLoadingTime
      await new Promise((resolve) => setTimeout(resolve, remainingTime));

      setSession(response.data);
    } catch (error) {
      console.error('Error fetching session:', error);
      // Handle error appropriately
    } finally {
      setIsLoading(false);
    }
  };

  return {
    genre: {
      get: genre,
      set: setGenre,
    },
    instrument: {
      get: instrument,
      set: setInstrument,
    },
    tempo: {
      get: tempo,
      set: setTempo,
    },
    density: {
      get: density,
      set: setDensity,
    },
    soundscape: {
      get: soundscape,
      set: setSoundscape,
    },
    session: {
      fetch: fetchSession,
      fetchByShareCode: fetchSessionByShareCode,
      get: session,
      isLoading: isLoading,
    },
  };
}
